import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Search, Settings as SettingsIcon } from 'react-feather';

import '../../ControlPanel/sadmin.css';
import './settings.css';

import CountriesTab from './tabs/CountriesTab';
import PropertiesTab from './tabs/PropertiesTab';
import CategoriesTab from './tabs/CategoriesTab';
import SubcategoriesTab from './tabs/SubcategoriesTab';

import CountryFormModal from './modals/CountryFormModal';
import PropertyFormModal from './modals/PropertyFormModal';
import CategoryFormModal from './modals/CategoryFormModal';
import SubcategoryFormModal from './modals/SubcategoryFormModal';

/**
 * /admin/settings — General Settings hub.
 *
 * State lives in the URL so deep links survive refresh, sharing and back-nav:
 *   ?tab=country|property|category|subcategory
 *   ?q=<search>
 *   ?page=<n>
 *   ?action=add | ?edit=<id>
 *
 * Each tab is a self-contained list + KPI strip. CRUD opens a modal whose
 * `onSaved` bumps `reloadKey` so the active tab re-fetches its data.
 *
 * Legacy routes (`/admin/country/addcountry/:layout`, `/admin/.../editcountry/:id`, etc.)
 * are mounted on this same component via Routes.jsx — the URL pattern is
 * detected in `useParams()` and translated into the equivalent
 * `?tab=…&action=add|edit=<id>` state, so bookmarked URLs keep working.
 */

const TABS = [
    { id: 'country', label: 'Countries', Component: CountriesTab, Modal: CountryFormModal },
    { id: 'property', label: 'Properties', Component: PropertiesTab, Modal: PropertyFormModal },
    { id: 'category', label: 'Categories', Component: CategoriesTab, Modal: CategoryFormModal },
    { id: 'subcategory', label: 'Subcategories', Component: SubcategoriesTab, Modal: SubcategoryFormModal },
];

const SettingsHub = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const searchInputRef = useRef(null);

    const manageGeneralSettings = localStorage.getItem('manageGeneralSettings');
    const accessGeneralSettings = localStorage.getItem('accessGeneralSettings');
    const canManage = manageGeneralSettings === '1';
    const canAccess = canManage || accessGeneralSettings === '1';

    const tab = searchParams.get('tab') || 'country';
    const q = searchParams.get('q') || '';
    const page = Number(searchParams.get('page')) || 1;
    const action = searchParams.get('action');
    const editId = searchParams.get('edit');

    // Local debounced mirror for the search input so typing doesn't push a
    // new history entry on every keystroke.
    const [qInput, setQInput] = useState(q);
    useEffect(() => { setQInput(q); }, [q]);
    useEffect(() => {
        const t = setTimeout(() => {
            if (qInput !== q) {
                const next = new URLSearchParams(searchParams);
                if (qInput) next.set('q', qInput); else next.delete('q');
                next.delete('page');
                setSearchParams(next, { replace: true });
            }
        }, 250);
        return () => clearTimeout(t);
    }, [qInput]);

    const [reloadKey, setReloadKey] = useState(0);
    const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

    const setParam = useCallback((patches, replace = false) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(patches).forEach(([k, v]) => {
            if (v == null || v === '') next.delete(k);
            else next.set(k, String(v));
        });
        setSearchParams(next, { replace });
    }, [searchParams, setSearchParams]);

    const switchTab = useCallback((nextTab) => {
        if (nextTab === tab) return;
        setSearchParams({ tab: nextTab });
    }, [tab, setSearchParams]);

    const onAdd = useCallback(() => setParam({ action: 'add', edit: null }), [setParam]);
    const onEdit = useCallback((id) => setParam({ action: null, edit: id }), [setParam]);
    const onCloseModal = useCallback(() => setParam({ action: null, edit: null }, true), [setParam]);

    // Translate legacy paths (kept for bookmark compatibility) into hub state.
    // Routes.jsx mounts SettingsHub on URLs like:
    //   /admin/country/viewcountry/:layout      → tab=country
    //   /admin/country/addcountry/:layout       → tab=country&action=add
    //   /admin/country/editcountry/:id          → tab=country&edit=:id
    //   /admin/country/countrydetail/:id        → tab=country&edit=:id (view = edit)
    //   …and equivalents for property / category / subcategory.
    useEffect(() => {
        const pathname = location.pathname || '';
        if (pathname.startsWith('/admin/settings')) return;
        const m = pathname.match(/^\/admin\/(country|property|category|subcategory)\/([^/]+)(?:\/([^/]+))?$/i);
        if (!m) return;
        const entity = m[1].toLowerCase();
        const verb = (m[2] || '').toLowerCase();
        const tail = m[3];
        const next = { tab: entity };
        // 'addcountry' / 'addproperty' / 'addcategory' / 'addsubcategory'
        if (verb.startsWith('add')) next.action = 'add';
        // 'editcountry' / 'editproperty' / etc. — :id lives in tail.
        else if (verb.startsWith('edit') && tail) next.edit = tail;
        // 'countrydetail' / 'propertydetail' — :id lives in tail. Fold to edit.
        else if (verb.endsWith('detail') && tail) next.edit = tail;
        // Replace the URL to the canonical /admin/settings location so the
        // back button doesn't bounce the user through the redirected path.
        navigate(`${process.env.PUBLIC_URL}/admin/settings?${new URLSearchParams(next).toString()}`, { replace: true });
    }, [location.pathname, navigate]);

    // Authorize. Use useEffect so the redirect doesn't fire during render.
    useEffect(() => {
        if (!canAccess) {
            window.alert(t('You are not authorized to view this page'));
            window.history.back();
        }
    }, [canAccess]);

    // Keyboard shortcut: "/" focuses the search input.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, []);

    const activeTab = useMemo(() => TABS.find((t) => t.id === tab) || TABS[0], [tab]);
    const TabComponent = activeTab.Component;
    const Modal = activeTab.Modal;
    const modalOpen = action === 'add' || !!editId;

    if (!canAccess) return null;

    return (
        <Fragment>
            <div className="v3-sadmin">
                {/* Hero ───────────────────────────────────────────────── */}
                <div className="v3-sadmin-hero">
                    <div className="v3-sadmin-hero-text">
                        <p className="v3-label" style={{ margin: 0 }}>{t('General settings')}</p>
                        <h1 className="v3-display v3-sadmin-greeting">
                            <span className="v3-sadmin-name">{t('Settings')}</span>
                        </h1>
                        <p className="v3-body v3-sadmin-sub">
                            {t('Manage your reference data — countries, properties, categories and subcategories — from one place.')}
                        </p>
                    </div>
                    <div className="v3-sadmin-hero-actions">
                        <span className="v3-sadmin-kpi-icon accent" aria-hidden>
                            <SettingsIcon size={18} />
                        </span>
                    </div>
                </div>

                {/* Hub card ───────────────────────────────────────────── */}
                <div className="v3-card v3-sadmin-list-card">
                    <div className="v3-settings-toolbar">
                        <div className="v3-settings-tabs" role="tablist">
                            {TABS.map((tb) => (
                                <button
                                    key={tb.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={tb.id === tab}
                                    className={`v3-settings-tab ${tb.id === tab ? 'active' : ''}`}
                                    onClick={() => switchTab(tb.id)}
                                >
                                    {t(tb.label)}
                                </button>
                            ))}
                        </div>
                        <label className="v3-sadmin-search">
                            <Search size={14} />
                            <input
                                ref={searchInputRef}
                                type="search"
                                value={qInput}
                                onChange={(e) => setQInput(e.target.value)}
                                placeholder={t('Search {{entity}}…  (/)', { entity: t(activeTab.label).toLowerCase() })}
                                aria-label={t('Search')}
                            />
                        </label>
                    </div>

                    <div style={{ padding: '20px 22px 4px' }}>
                        <TabComponent
                            q={q}
                            page={page}
                            onPageChange={(p) => setParam({ page: p > 1 ? p : null })}
                            onEdit={onEdit}
                            onAdd={onAdd}
                            canManage={canManage}
                            reloadKey={reloadKey}
                        />
                    </div>
                </div>
            </div>

            {modalOpen && (
                <Modal
                    mode={action === 'add' ? 'add' : 'edit'}
                    entityId={editId}
                    onClose={onCloseModal}
                    onSaved={refresh}
                />
            )}
        </Fragment>
    );
};

export default SettingsHub;
